import axios from "axios";
import api from "./api";
import { DataValidator, IsString, Required, transformKeys } from "@/utils/type-vaildator";

/** 获取文件上传链接接口参数类型 */
export interface GetUploadUrlParams {
  file_name: string;
  file_size: number;
  content_type: string;
}

/** 获取文件上传链接口数据类型 */
export interface GetUploadUrlProps {
  fileName: string;
  fileSize: number;
  contentType: string;
}

export interface UploadFileProps {
  file: File;
  onProgress?: (progress: number) => void;
}

/** 文件上传链接响应类型 */
export interface UploadUrlResponse {
  content_type: string;
  expires_in: number;
  file_type: string;
  file_uri: string;
  key: string;
  message: string;
  presigned_url: string;
  task_id: string;
}

/** 文件上传链接响应数据 */
export class UploadUrlData {
  @Required("ContentType cannot be empty")
  @IsString("ContentType must be a string")
  contentType: string;

  @Required("PresignedUrl cannot be empty")
  @IsString("PresignedUrl must be a string")
  presignedUrl: string;

  @Required("TaskId cannot be empty")
  @IsString("TaskId must be a string")
  taskId: string;
}

/** 文件下载链接响应类型 */
export interface DownloadUrlResponse {
  file_type: string;
  file_uri: string;
  key: string;
  task_id: string;
}

/** 文件下载链接响应数据 */
export class DownloadUrlResponseData {
  @Required("FileType cannot be empty")
  @IsString("FileType must be a string")
  fileType: string;

  @Required("FileUri cannot be empty")
  @IsString("FileUri must be a string")
  fileUri: string;

  @Required("TaskId cannot be empty")
  @IsString("TaskId must be a string")
  taskId: string;
}

const transformAndValidateUploadUrl = (
  response: UploadUrlResponse
): UploadUrlData => {
  const transformedData = transformKeys(response);

  try {
    return DataValidator.validateOrThrow(transformedData, UploadUrlData);
  } catch (error) {
    console.error("Upload url data validation failed:", error.message);
    throw error;
  }
};

const transformAndValidateDownloadUrl = (
  response: DownloadUrlResponse
): DownloadUrlResponseData => {
  const transformedData = transformKeys(response);

  try {
    return DataValidator.validateOrThrow(transformedData, DownloadUrlResponseData);
  } catch (error) {
    console.error("Download url data validation failed:", error.message);
    throw error;
  }
};

/**
 * 获取文件上传链接
 * @param fileName 文件名
 * @param fileSize 文件大小
 * @param contentType 文件类型
 * @returns Promise<UploadUrlResponse>
 */
export const getUploadUrl = async ({
  fileName,
  fileSize,
  contentType,
}: GetUploadUrlProps) => {
  const params: GetUploadUrlParams = {
    file_name: fileName,
    file_size: fileSize,
    content_type: contentType,
  };
  const response = await api.post<UploadUrlResponse>("/upload/file", params);
  return transformAndValidateUploadUrl(response.data);
};

/**
 * 获取文件下载链接
 * @param taskId 任务ID
 * @returns Promise<DownloadUrlResponse>
 */
export const getDownloadUrl = async (taskId: string) => {
  const response = await api.post<DownloadUrlResponse>(
    `/upload/confirm/${taskId}`
  );
  return response.data;
};

/**
 * 文件上传
 * @param file 要上传的文件
 * @param onProgress 上传进度回调函数
 * @returns Promise<DownloadUrlResponse>
 */
export const upload = async ({ file, onProgress }: UploadFileProps) => {
  // 读取文件为 ArrayBuffer
  const fileBinary: ArrayBuffer = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as ArrayBuffer);
    reader.onerror = (e) => reject(e);
    reader.readAsArrayBuffer(file);
  });

  // 1.获取上传链接
  const data = await getUploadUrl({
    fileName: file.name,
    fileSize: file.size,
    contentType: file.type.split("/")[0],
  });

  // 2.正式上传
  await axios.put(`${data.presignedUrl}`, fileBinary, {
    headers: {
      "Content-Type": data.contentType,
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const loaded = (progressEvent.loaded * 100) / progressEvent.total;
        const progress = Math.round(loaded);
        onProgress(progress);
      }
    },
  });
  // 获取下载链接
  const downloadData = await getDownloadUrl(data.taskId);
  return transformAndValidateDownloadUrl(downloadData);
};
